import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TextField, Button, Box, Typography, InputAdornment, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { Padding, Search } from '@mui/icons-material';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import { useNavigate } from "react-router-dom";
import Chip from '@mui/material/Chip';
import { apiUrl } from '../utils/api';
import { addRecentJob, readRecentJobs } from '../utils/jobCookies';



// Precalculated demo jobs -- clicking one jumps straight to its finished
// result instead of filling the search box and requiring the user to run a
// fresh prediction (which would take a while for these deliberately larger
// example structures).
const PRECALCULATED_EXAMPLES = [
  { id: "6MOJ", jobId: "a310e61c20094cedb68d2180f34c4f35" },
  { id: "2AJF", jobId: "97723420811042718b5a2a3e34932f82" },
  { id: "7DHX", jobId: "9207284941d34cd49f93627c46783bc8" }
];

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  // Below this, the Upload/Run buttons don't fit inside the text field's
  // end adornment alongside the placeholder text -- they get pushed out
  // and clipped by the viewport edge. Moved to their own row instead.
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  const [search, setSearch] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [pdbFile, setPdbFile] = React.useState(null);
  const [status, setStatus] = React.useState("idle"); 
  // idle | loading | success | error
  const [statusMsg, setStatusMsg] = React.useState("");
  const [recentJobs, setRecentJobs] = React.useState([]);

  useEffect(() => {
    setRecentJobs(readRecentJobs());
  }, []);


  const handleRunPrediction = async () => {
  try {
    // 🔵 START: loading
    setStatus("loading");
    setStatusMsg("Uploading input and preparing prediction…");

    const formData = new FormData();
    

    if (pdbFile) {
      formData.append("file", pdbFile);
      setStatusMsg("Uploading PDB file…");
    } else {
      formData.append("pdb_id", search);
      setStatusMsg("Fetching PDB structure…");
    }

    const response = await fetch(apiUrl("/predict"), {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) throw new Error("Prediction failed");

    const data = await response.json();


    console.log("Backend result:", data);

    // 🟢 SUCCESS
    setStatus("success");
    setStatusMsg("Prediction started. You can return to the dedicated result link when it is ready.");

    const jobName = search || pdbFile?.name || data.pdb_name || "prediction";
    const updatedJobs = addRecentJob({
      job_id: data.job_id,
      pdb_name: jobName,
      result_url: data.result_url,
      created_at: new Date().toISOString(),
    });
    setRecentJobs(updatedJobs);

    // small delay so user sees success
    setTimeout(() => {
      navigate(`/result/${data.job_id}`, {
        state: {
          jobId: data.job_id,
          pdbName: jobName,
        },
      });
    }, 600);

  } catch (error) {
    console.error(error);
    // 🔴 ERROR
    setStatus("error");
    setStatusMsg("Prediction failed. Please check your input.");

    setPdbFile(null);
    setSearch("");

  }
};

  const uploadButton = (
    <Button
      variant="outlined"
      component="label"
      sx={{
        height: "38px",
        minWidth: "110px",
        flex: { xs: 1, sm: "0 0 auto" },
        borderRadius: "6px",
        fontSize: "0.85rem",
        whiteSpace: "nowrap",
        borderColor: "#1976d2",
        color: "#1976d2",
        "&:hover": {backgroundColor: "#e3f2fd"}
      }}
    >
      UPLOAD PDB
      <input type="file" hidden accept='.pdb'
        onChange={(e) => {
          const file = e.target.files[0];
          if (!file) return;

          setPdbFile(file);
          setSearch(file.name.replace(".pdb", ""));
        }}
      />
    </Button>
  );

  const runButton = (
    <Button
      variant="contained"
      disabled={status === "loading" || (!search.trim() && !pdbFile)}
      sx={{
        height: "38px",
        minWidth: "140px",
        flex: { xs: 1, sm: "0 0 auto" },
        borderRadius: "6px",
        backgroundColor: "#212c45",
        fontSize: "0.85rem",
        whiteSpace: "nowrap",
        color: "#fff",
        fontWeight: 500,
        "&:hover": { backgroundColor: "#115293" },
        "&.Mui-disabled": {
          backgroundColor: "#9e9e9e", // nicer disabled look
          color: "#eee",
        },
      }}
      onClick={() => {
        handleRunPrediction();
      }}
    >
      {status === "loading" ? (
        <CircularProgress size={18} sx={{ color: "#fff" }} />
      ) : (
        "RUN PREDICTION"
      )}
    </Button>
  );

  return (
    <Box component="section" className="hero">
      <Box className="hero-layer">
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h3" gutterBottom>predPPI-GReMLIN</Typography>
          <Typography variant="h6" gutterBottom>predict protein-protein interactions directly from 3D structures using atomic-level interface patterns and graph mining</Typography>
          {/* <Button component={Link} to="/about" variant="contained">Learn More</Button> */}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: { xs: "100%", sm: "85%", md: "60%" }, maxWidth: 1200, margin: "0 auto", mt: 6 }}>

          <TextField
            fullWidth
            placeholder="Enter a PDB ID or upload a PDB file"
            variant="outlined"
            size="medium"

            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#888" }} />
                </InputAdornment>
              ),
              // Below `sm`, the Upload/Run buttons don't fit inside the
              // adornment next to the placeholder text -- they get pushed
              // past the field's edge and clipped by the viewport. Rendered
              // as their own full-width row underneath instead (see below).
              endAdornment: isCompact ? null : (
                <InputAdornment position="end">
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {uploadButton}
                    {runButton}
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              borderColor: "blue",
              backgroundColor: "#fff",
              borderRadius: "6px",

            }}


          />

          {isCompact && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {uploadButton}
              {runButton}
            </Box>
          )}

        </Box>
        <Box sx={{ textAlign: 'left', mb: 2, display: 'flex', flexWrap: "wrap", justifyContent: "flex-start", alignItems: "flex-start", width: { xs: "100%", sm: "85%", md: "60%" }, maxWidth: 1200, paddingTop: 2}}>
          <Typography variant="body1" gutterBottom style={{paddingTop:5, paddingRight: 10, fontWeight:"bolder"}}>Precalculated Examples: </Typography>
          {/* <Button component={Link} to="/about" variant="contained">Learn More</Button> */}
          <ButtonGroup
            variant="outlined"
            aria-label="Basic button group"
            sx={{
              "& .MuiButton-root": {
                borderColor: "#ccc",
                borderWidth: "2px",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#aaa",
                  color: "#000",
                },
                marginRight: "8px",
                "&:last-of-type": {
                  marginRight: 0,
                },
              },
            }}
          >
            {PRECALCULATED_EXAMPLES.map(({ id, jobId }) => (
              <Button
                key={id}
                onClick={() => navigate(`/result/${jobId}`, { state: { jobId, pdbName: id } })}
              >
                {id}
              </Button>
            ))}
          </ButtonGroup>

        </Box>
        {status !== "idle" && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "center",
              }}
            >
              {status === "loading" && (
                <>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {statusMsg}
                  </Typography>
                </>
              )}

              {status === "success" && (
                <Typography variant="body2" sx={{ color: "green", fontWeight: 500 }}>
                  ✔ {statusMsg}
                </Typography>
              )}

              {status === "error" && (
                <Typography variant="body2" sx={{ color: "red", fontWeight: 500 }}>
                  ✖ {statusMsg}
                </Typography>
              )}
            </Box>
          )}

        {recentJobs.length > 0 && (
          <Box sx={{ mt: 3, width: { xs: "100%", sm: "85%", md: "60%" }, maxWidth: 1200, mx: "auto" }}>
            <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
              Recent Predictions
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {recentJobs.map(job => (
                <Button
                  key={job.job_id}
                  component={Link}
                  to={`/result/${job.job_id}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: "#fff",
                    borderColor: "#fff",
                    backgroundColor: "rgba(33, 44, 69, 0.55)",
                    "&:hover": {
                      borderColor: "#fff",
                      backgroundColor: "rgba(255, 255, 255, 0.14)",
                    },
                  }}
                >
                  {job.pdb_name || job.job_id?.slice(0, 8) || "Prediction"}
                </Button>
              ))}
            </Box>
          </Box>
        )}

      </Box>
      
      <Box className="hero-layer2">
        <Box sx={{ textAlign: 'left', fontWeight:500, flex: 1}}>
          <Typography variant="h5" gutterBottom>What This Tool Does</Typography>
          <Typography variant="h6" gutterBottom>Predict Protein-Protein Interactions from Structure</Typography>
          <Typography variant="body1" gutterBottom>predPPI-GReMLIN analyzes protein complexes at the <b>atomic interface level</b> to determine whether 
            two proteins interact and identify <b>potentential binding partners (Ligands)</b>.</Typography>

          <Typography variant="body1" gutterBottom style={{paddingTop:10}}> Instead of relying on sequence similarity alone, the tool:</Typography>

          <Box component="ul" sx={{ paddingLeft: "1.5rem", marginTop: 0 }}>
            <li>Uses 3D structural information of known protein complexes</li>
            <li>Models protein interfaces as <b>bipartite graphs</b></li>
            <li>Searches for <b>conserved binding partners</b> observed in graph corpus</li>
          </Box>

          <Typography style={{paddingTop:5}}>This enables accurate, interpretable PPI prediction even for novel or proteins.</Typography>
          {/* <Button component={Link} to="/about" variant="contained">Learn More</Button> */}
        </Box>

        <Box sx={{fontWeight:500, flex: 1, alignItems: "center", }} >
          <Box
            component="img"
            src="/image/ppi4.png"
            alt="Description"
            sx={{ width: "100%", height: "auto",maxWidth: "600px"}}
          />

        </Box>
        

        
      </Box>
      <Box className="hero-layer3">
          <Divider>
            <Chip label="predPPI-GReMLIN Workflow" size="medium" sx={{fontSize: "1.5rem", fontWeight: "500", px: 1.5}} />
          </Divider>
      </Box>
      <Box className="hero-layer4">
          <Box
            component="img"
            src="/image/search.png"
            alt="Description"
            sx={{ width: { xs: "80%", sm: "60%", md: "40%" } }}
          />
          <Box
            component="img"
            src="/image/swap.png"
            alt="Description"
            sx={{ width: { xs: "80%", sm: "60%", md: "40%" } }}
          />
      </Box>
      <Box className="hero-layer3" sx={{textAlign:"justify"}}>
        <Typography>
          In (A), a query graph (representing
          an interface pattern of interest) is searched against a universe of
          protein–protein complexes, referred to as the graph corpus, to identify
          those graphs that contain the same pattern as the query graph. Our
          hypothesis is that the molecule associated with the ligand side of the
          graphs in the graph pattern match result is a potential ligand for the
          molecule on the target side of the query graph. In (B), the process by which
          a ligand swap can potentially be performed—from the original ligand in
          the query graph to the predicted ligand obtained from the graph pattern
          match result—is illustrated.
        </Typography>
      </Box>



      
      <Box className="hero-layer3">
        <Divider>
          <Chip label="Demo Preview" size="medium" sx={{fontSize: "1.5rem", fontWeight: "500", px: 1.5}} />
        </Divider>
      </Box>

      <Box className="hero-layer5">
        <Box
          sx={{
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            p: 2,
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              width: "100%",
              overflow: "hidden",
              borderRadius: "12px",
            }}
          >
            <Box
              component="iframe"
              src="https://www.loom.com/embed/bf5218de535f40a4a6546e13c0213115?autoplay=1&muted=1"
              title="Demo"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; fullscreen; picture-in-picture"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/*<Box className="hero-layer3">
            <Divider>
              <Chip label="Data Visualization" size="medium" sx={{fontSize: "1.5rem", fontWeight: "500", px: 1.5}} />
            </Divider>
      </Box>

      <Box className="hero-layer5">
        <Box
            component="img"
            src="/image/nviz3.png"
            alt="Description"
            sx={{ width: "100%" }}
        />
      </Box>
      <Box className="hero-layer5">
        <Box
            component="img"
            src="/image/nviz.png"
            alt="Description"
            sx={{ width: "70%" }}
        />
          
        <Box
            component="img"
            src="/image/nviz2.png"
            alt="Description"
            sx={{ width: "30%" }}
        /> 
        
      </Box>*/}
      
      

    </Box>

  );
};

export default Home;
